// src/components/AIAssistant.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { X, MessageSquare, ChevronRight, Info, Activity, 
         Send, Mic, Paperclip, Settings, Copy, 
         ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react';
import { ThemeContext, AlienContext } from '../App';
import { nanoid } from 'nanoid';
import './AIAssistant.css';

const AIAssistant = () => {
  // Context
  const { darkMode } = useContext(ThemeContext);
  const { toggleAlien } = useContext(AlienContext);
  
  // State
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  
  // Refs
  const chatBodyRef = useRef(null);
  const inputRef = useRef(null);
  const messageEndRef = useRef(null);

  // Initialize and show tooltip
  useEffect(() => {
    if (chatOpen) return;
    
    // Show tooltip after delay
    const tooltipTimer = setTimeout(() => {
      const randomMsg = getRandomAssistantMessage();
      setTooltipMessage(randomMsg);
      setTooltipVisible(true);
      
      // Hide tooltip after a delay
      setTimeout(() => {
        setTooltipVisible(false);
      }, 8000);
    }, 5000);
    
    return () => {
      clearTimeout(tooltipTimer);
    };
  }, [chatOpen]);
  
  // Handle initial messages
  useEffect(() => {
    // Add welcome message if no messages
    if (messages.length === 0) {
      setMessages([
        { 
          id: nanoid(),
          sender: 'assistant', 
          text: getRandomAssistantMessage(),
          timestamp: Date.now(),
          status: 'delivered'
        }
      ]);
    }
  }, [messages]);

  // Handle auto-scrolling when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  
  // Get a random message for the assistant
  const getRandomAssistantMessage = () => {
    const messages = [
      "How can I assist you with finding information about the Grand Lodge?",
      "Welcome to the Grand Lodge. I'm here to help answer your questions about Freemasonry.",
      "Interested in learning more about Masonic traditions? I can provide you with resources and information.",
      "Looking for a local lodge? I can help you find one in your area.",
      "What aspects of Freemasonry would you like to learn more about today?"
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  };
  
  // Scroll to bottom of chat
  const scrollToBottom = () => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Toggle chat open/closed
  const toggleChat = () => {
    setChatOpen(prev => !prev);
  };

  // Get a response based on the user input
  const getMasonicResponse = (input) => {
    const lowerInput = input.toLowerCase();
    
    if (lowerInput.includes('hi') || lowerInput.includes('hello') || lowerInput.includes('hey')) {
      return "Hello! I'm the Grand Lodge's AI assistant. How can I help you today?";
    } else if (lowerInput.includes('lodge') || lowerInput.includes('grand lodge')) {
      return "The Grand Lodge has a rich history dating back to 1818. We have over 200 constituent lodges throughout the state. Would you like to know more about our organization or find a lodge near you?";
    } else if (lowerInput.includes('join') || lowerInput.includes('become') || lowerInput.includes('member')) {
      return "To become a Mason, you need to be a man of good character, at least 21 years of age, believe in a Supreme Being, and be recommended by current members. The process involves submitting an application, receiving an investigation committee visit, and going through the three degrees of Masonry. Would you like more information?";
    } else if (lowerInput.includes('history') || lowerInput.includes('origin') || lowerInput.includes('begin')) {
      return "Freemasonry evolved from medieval stonemasons' guilds into a philosophical organization focused on moral development. Our Grand Lodge was chartered in 1818, making it one of the older Grand Lodges in the United States. The symbols and traditions of Freemasonry derive from ancient architectural practices and philosophical principles. Would you like to know more about a specific aspect of our history?";
    } else {
      return "That's an interesting question about Freemasonry. I'd be happy to help you explore this topic further. Our Grand Lodge has extensive resources on this subject. What specific aspects would you like to know about?";
    }
  };

  // Handle sending a message
  const sendMessage = () => {
    if (!userInput.trim()) return;
    
    // Add user message
    const userMessage = { 
      id: nanoid(),
      sender: 'user', 
      text: userInput.trim(),
      timestamp: Date.now(),
      status: 'sent'
    };
    
    setMessages(prev => [...prev, userMessage]);
    setUserInput('');
    setIsTyping(true);
    
    // Simulate typing delay
    setTimeout(() => {
      // Add assistant message
      const assistantMessage = { 
        id: nanoid(),
        sender: 'assistant', 
        text: getMasonicResponse(userMessage.text),
        timestamp: Date.now(),
        status: 'delivered'
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
      
      // Generate suggestions
      generateSuggestions(assistantMessage.text);
    }, 1500);
  };
  
  // Generate follow-up suggestions
  const generateSuggestions = (lastResponse) => {
    // Simple suggestions based on the response content
    const lowerResponse = lastResponse.toLowerCase();
    let suggestions = [];
    
    if (lowerResponse.includes('lodge')) {
      suggestions.push('Where is the nearest lodge?');
      suggestions.push('What are lodge meetings like?');
    }
    
    if (lowerResponse.includes('history')) {
      suggestions.push('When was Freemasonry founded?');
      suggestions.push('Notable Masonic historical figures');
    }
    
    if (lowerResponse.includes('become') || lowerResponse.includes('join')) {
      suggestions.push('What are the requirements to join?');
      suggestions.push('How long does the process take?');
    }
    
    // Always add some general suggestions
    if (suggestions.length < 2) {
      suggestions.push('What are Masonic symbols?');
      suggestions.push('Tell me about Masonic charity work');
    }
    
    // Limit to 3 suggestions
    setAiSuggestions(suggestions.slice(0, 3));
  };
  
  // Handle pressing Enter to send
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setUserInput(suggestion);
    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  // Handle message reactions
  const handleMessageReaction = (messageId, reaction) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? {
        ...msg,
        reaction
      } : msg
    ));
  };
  
  // Clear chat history
  const clearChat = () => {
    const welcomeMessage = { 
      id: nanoid(),
      sender: 'assistant', 
      text: getRandomAssistantMessage(),
      timestamp: Date.now(),
      status: 'delivered'
    };
    
    setMessages([welcomeMessage]);
    setAiSuggestions([]);
  };
  
  return (
    <div 
      className={`ai-assistant-container ${darkMode ? 'dark' : ''}`} 
      aria-label="AI Assistant Interface"
    >
      {/* Tooltip for suggestions */}
      <div className={`ai-tooltip ${tooltipVisible ? 'visible' : ''}`}>
        {tooltipMessage}
      </div>
      
      {/* Main assistant button */}
      <button 
        className="ai-assistant-button" 
        onClick={toggleChat}
        aria-label="Toggle AI assistant"
      >
        {chatOpen ? (
          <X size={24} />
        ) : (
          <>
            <div className="pulsating-dot"></div>
            <div className="waveform">
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
            </div>
          </>
        )}
      </button>
      
      {/* Chat panel */}
      {chatOpen && (
        <div className={`ai-chat-panel open`}>
          {/* Chat header */}
          <div className="ai-chat-header">
            <div className="ai-chat-title">
              <Activity size={20} />
              <span>Mason AI Assistant</span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                className="ai-header-btn"
                onClick={() => setShowSettings(!showSettings)} 
                aria-label="Settings"
              >
                <Settings size={18} />
              </button>
              <button 
                className="ai-close-btn" 
                onClick={toggleChat}
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          
          {/* Settings panel */}
          {showSettings && (
            <div className="ai-settings-panel">
              <div className="ai-settings-header">
                <h3>Assistant Settings</h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  aria-label="Close settings"
                >
                  <X size={16} />
                </button>
              </div>
              
              <div className="ai-settings-content">
                <div className="setting-group">
                  <button 
                    className="setting-action-btn danger"
                    onClick={clearChat}
                  >
                    <RefreshCw size={16} />
                    <span>Clear Conversation</span>
                  </button>
                </div>
                
                <div className="setting-version">
                  <span>AI Assistant v3.2.1</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Chat body */}
          <div 
            className="ai-chat-body"
            ref={chatBodyRef}
          >
            {/* Messages */}
            {messages.map(message => (
              message.sender === 'assistant' ? (
                <div key={message.id} className="ai-message assistant">
                  <div className="ai-message-avatar">
                    <Activity size={16} />
                  </div>
                  <div className="ai-message-content">
                    {message.text}
                    
                    <div className="message-actions">
                      <button 
                        onClick={() => navigator.clipboard.writeText(message.text)} 
                        className="text-gray-400 hover:text-gray-600 p-1 rounded"
                        aria-label="Copy message"
                      >
                        <Copy size={14} />
                      </button>
                      <button 
                        onClick={() => handleMessageReaction(message.id, 'thumbsUp')} 
                        className="text-gray-400 hover:text-blue-500 p-1 rounded"
                        aria-label="Like message"
                      >
                        <ThumbsUp size={14} />
                      </button>
                      <button 
                        onClick={() => handleMessageReaction(message.id, 'thumbsDown')} 
                        className="text-gray-400 hover:text-red-500 p-1 rounded"
                        aria-label="Dislike message"
                      >
                        <ThumbsDown size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={message.id} className="ai-message user">
                  <div className="ai-message-content">
                    {message.text}
                  </div>
                  <div className="ai-message-avatar">
                    <div>U</div>
                  </div>
                </div>
              )
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="ai-message assistant">
                <div className="ai-message-avatar">
                  <Activity size={16} />
                </div>
                <div className="ai-message-content">
                  <div className="typing-indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            )}
            
            {/* AI Suggestions */}
            {aiSuggestions.length > 0 && (
              <div className="ai-suggestions">
                {aiSuggestions.map((suggestion, index) => (
                  <button 
                    key={index} 
                    className="ai-suggestion-chip"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            
            {/* Scroll anchor */}
            <div ref={messageEndRef} />
          </div>
          
          {/* Chat input */}
          <div className="ai-chat-input">
            <div className="ai-input-wrapper">
              <input 
                type="text" 
                placeholder="Ask a question about Freemasonry..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                ref={inputRef}
              />
            </div>
            
            <button 
              className="ai-send-btn"
              onClick={sendMessage} 
              disabled={!userInput.trim()}
              aria-label="Send message"
            >
              <Send size={18} />
            </button>
          </div>
          
          {/* Footer */}
          <div className="ai-chat-footer">
            <div className="powered-by">
              Powered by Grand Lodge of Mississippi
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;